import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { PricingPaywallDialog } from "@/components/paywall/PricingPaywallDialog";
import { ANALYTICS_EVENTS, trackEvent } from "@/lib/analytics";

const PaywallContext = createContext(null);

/**
 * @typedef {{ jobTitle?: string, company?: string, jobId?: string, dismissTo?: string | null, otherRolesCount?: number, quickProfile?: object, source?: string }} PaywallPayload
 */

export function PaywallProvider({ children }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  const openPaywall = useCallback((p) => {
    const next = p && typeof p === "object" ? p : {};
    setPayload(next);
    setOpen(true);
    trackEvent(ANALYTICS_EVENTS.FUNNEL_PAYWALL_OPEN, {
      source: next.source || "paywall",
      job_id: next.jobId != null ? String(next.jobId) : undefined,
      plan_context: next.quickProfile ? "personalized" : "default",
    });
  }, []);

  const closePaywall = useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  /** Close overlay only (no dismissTo navigation) — e.g. in-app links to /pricing. */
  const closePaywallQuiet = useCallback(() => {
    setOpen(false);
    setPayload(null);
  }, []);

  const value = useMemo(
    () => ({ openPaywall, closePaywall, closePaywallQuiet }),
    [openPaywall, closePaywall, closePaywallQuiet]
  );

  const handleOpenChange = (next) => {
    if (!next) {
      const to = payload?.dismissTo;
      if (typeof to === "string" && to.length > 0) {
        navigate(to);
      }
      setOpen(false);
      setPayload(null);
    }
  };

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PricingPaywallDialog
        open={open}
        onOpenChange={handleOpenChange}
        paywallPayload={payload ?? {}}
        onCloseQuiet={closePaywallQuiet}
      />
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const ctx = useContext(PaywallContext);
  if (!ctx) {
    throw new Error("usePaywall must be used within PaywallProvider");
  }
  return ctx;
}
